import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { IHandlers } from "@dhx/ts-common/types";
import { View, toViewLike, IView, } from "@dhx/ts-common/view";
import { Layout } from "@dhx/ts-layout";
import { Slider, SliderEvents } from "@dhx/ts-slider";
import locale from "./locales/en";
import { isTimeCheck } from "./helper";
import { TimepickerEvents, ITimepickerConfig, ITimepicker, ITimeObject } from "./types";

export class Timepicker extends View implements ITimepicker {
	public config: ITimepickerConfig;
	public events: IEventSystem<TimepickerEvents>;

	public layout: Layout;

	private _hoursSlider: Slider;
	private _minutesSlider: Slider;

	private _inputsView: IView;

	private _time: {
		h: number,
		m: number;
		isAM: boolean;
	};

	private _handlers: IHandlers;
	private _outerHandlers;

	constructor(container: HTMLElement | string, config: ITimepickerConfig = {}) {
		super(container, extend({
			timeFormat: 24,
			actions: false
		}, config));

		this.events = new EventSystem(this);

		this._time = {
			h: 0,
			m: 0,
			isAM: true
		};
		if (this.config.timeFormat === 12) {
			this._time.h = 12;
		}
		this._initUI(container);
		this._initHandlers();
		this._initEvents();
	}
	getValue<T extends boolean = false>(asOBject?: T): T extends true ? ITimeObject : string {
		const {h, m, isAM} = this._time;
		if (asOBject) {
			const obj: {hour: number, minute: number, AM?: boolean} = {
				hour: h,
				minute: m
			};
			if (this.config.timeFormat === 12) {
				obj.AM = isAM;
			}
			return obj as any;
		}
		return (h < 10 ? "0" + h : h) + ":" + (m < 10 ? "0" + m : m) + (this.config.timeFormat === 12 ? (isAM ? "AM" : "PM") : "") as any;
	}
	setValue(value: Date | number | string | any[]): void {
		let m: number;
		let h: number;
		let isPM: boolean;
		if (typeof value === "number") {
			value = new Date(value);
		}
		if (value instanceof Date) {
			m = value.getMinutes();
			h = value.getHours();
		} else if (Array.isArray(value)) {
			h = validate(value[0], 23);
			m = validate(value[1], 59);
			if (value[2] && value[2].toLowerCase() === "pm") {
				isPM = true;
			}
		} else {
			const matches = value.match(/\d+/g);
			h = validate(+matches[0], 23);
			m = validate(+matches[1], 59);
			if (value.toLowerCase().indexOf("pm") !== -1) {
				isPM = true;
			}
		}
		if (isPM && h < 12) {
			h += 12;
		}
		this._hoursSlider.setValue(h);
		this._minutesSlider.setValue(m);

		if (isTimeCheck(value)) {
			this._hoursSlider.setValue(0);
			this._minutesSlider.setValue(m);
			this._time.isAM = true;
		}

		this._inputsView.paint();
	}
	destructor(): void {
		this._minutesSlider.destructor();
		this._hoursSlider.destructor();
		this.events.clear();
		this.unmount();
	}
	getRootView() {
		return this.layout.getRootView();
	}
	private _initUI(container) {
		const layoutConfig = {
			gravity: false,
			css: "dhx_widget dhx_timepicker " +
			(this.config.css ? this.config.css : "") +
			(this.config.actions ? " dhx_timepicker--with-actions" : ""),
			rows: [
				{
					id: "timepicker",
					css: "dhx_timepicker__inputs"
				},
				{
					id: "hour-slider",
					css: "dhx_timepicker__hour"
				},
				{
					id: "minute-slider",
					css: "dhx_timepicker__minute"
				}
			]
		};
		if (this.config.actions) {
			layoutConfig.rows.unshift({
				id: "close-action",
				css: "dhx_timepicker__close"
			});
			layoutConfig.rows.push({
				id: "save-action",
				css: "dhx_timepicker__save"
			});
		}
		const layout = this.layout = new Layout(container, layoutConfig);

		const timepicker = create({
			render: () => this._draw()
		});
		const inputsView = this._inputsView = toViewLike(timepicker);

		const mSlider = this._minutesSlider = new Slider(null, {
			min: 0,
			max: 59,
			step: 1,
			thumbLabel: false,
			labelInline: false,
			label: locale.minutes
		});

		const hSlider = this._hoursSlider = new Slider(null, {
			min: 0,
			max: 23,
			step: 1,
			thumbLabel: false,
			labelInline: false,
			label: locale.hours
		});

		layout.getCell("timepicker").attach(inputsView);
		layout.getCell("hour-slider").attach(hSlider);
		layout.getCell("minute-slider").attach(mSlider);

		if (this.config.actions) {
			const save = () => {
				return el("button.dhx_timepicker__button-save.dhx_button.dhx_button--view_flat.dhx_button--color_primary.dhx_button--size_medium.dhx_button--circle.dhx_button--width_full",
				{ onclick: this._outerHandlers.save }, locale.save);
			};
			const close = () => {
				return el("button.dhx_timepicker__button-close.dhx_button.dhx_button--view_link.dhx_button--size_medium.dhx_button--view_link.dhx_button--color_secondary.dhx_button--icon.dhx_button--circle",
				{
					onclick: this._outerHandlers.close
				}, [el("span.dhx_button__icon.dxi.dxi-close")]);
			};
			layout.getCell("save-action").attach(save);
			layout.getCell("close-action").attach(close);
		}
	}
	private _initHandlers() {
		this._handlers = {
			onchange: {
				".dhx_timepicker-input--hour": e => {
					const hour = validate(parseInt(e.target.value, 10), 23);
					e.target.value = hour;
					this._hoursSlider.setValue(hour);
				},
				".dhx_timepicker-input--minutes": e => {
					const min = validate(parseInt(e.target.value, 10), 59);
					e.target.value = min;
					this._minutesSlider.setValue(min);
				}
			}
		};
		this._outerHandlers = {
			close: () => this.events.fire(TimepickerEvents.close),
			save: () => this.events.fire(TimepickerEvents.save, [this._time])
		};
	}
	private _initEvents() {
		this._hoursSlider.events.on(SliderEvents.change, value => {
			if (value < this._hoursSlider.config.min || value > this._hoursSlider.config.max) {
				return;
			}
			if (this.config.timeFormat === 12) {
				this._time.isAM = value < 12;
				this._time.h = value % 12 || 12;
			} else {
				this._time.h = value;
			}
			this.events.fire(TimepickerEvents.change, [this.getValue()]);
			this._inputsView.paint();
		});
		this._minutesSlider.events.on(SliderEvents.change, value => {
			if (value < this._minutesSlider.config.min || value > this._minutesSlider.config.max) {
				return;
			}
			this._time.m = value;
			this.events.fire(TimepickerEvents.change, [this.getValue()]);
			this._inputsView.paint();
		});
	}
	private _draw() {
		return el(".dhx_timepicker-inputs", {
			...this._handlers
		}, [
			el("input.dhx_timepicker-input.dhx_timepicker-input--hour", {
				_key: "hour",
				value: this._time.h < 10 ? "0" + this._time.h : this._time.h
			}),
			el("span.dhx_timepicker-delimer", ":"),
			el("input.dhx_timepicker-input.dhx_timepicker-input--minutes", {
				_key: "minute",
				value: this._time.m < 10 ? "0" + this._time.m : this._time.m
			}),
			this.config.timeFormat === 12 ? el(".dhx_timepicker-ampm", this._time.isAM ? "AM" : "PM") : null
		]);
	}
}

function validate(value: number, max: number): number {
	if (isNaN(value)) {
		return 0;
	}
	return Math.min(max, Math.max(0, value));
}